import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBhajanDataRequest, selectCatalog, selectStatus } from '../Features/BhajanSlice';
import type { AppDispatch } from '../store/store';
import type { AnyGraphNode } from '../types/graphNodes';
import type { FilterDefinition } from '../filters/types';
import { FilterBar } from '../filters/components/FilterBar';
import { useFilterSet } from '../filters/hooks/useFilterSet';
import { useFilterUrl } from '../filters/hooks/useFilterUrl';
import { useFilteredNodes } from '../filters/hooks/useFilteredNodes';
import { nameFilter, nodeIdFilter } from '../filters/definitions';
import { getFacet, facetFilterHref } from '../browse/facets';
import { deriveFacetValues, type FacetValue } from '../browse/deriveFacetValues';
import { orderResults } from '../browse/resultOrdering';
import { APP } from '../app/appConfig';

// Same faceted-search experience as the bhajan browser, but the only searchable
// dimension here is the value's own name (these are leaf metadata nodes).
const facetDefinitions: Record<string, FilterDefinition> = {
  name: nameFilter,
  nodeId: nodeIdFilter,
};

const MetadataBrowseList: React.FC = () => {
  const { facet: facetKey } = useParams<{ facet: string }>();
  const facet = getFacet(facetKey);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const catalog = useSelector(selectCatalog);
  const status = useSelector(selectStatus);

  const { activeFilters, setFilters } = useFilterSet();
  useFilterUrl(activeFilters, setFilters);

  useEffect(() => { if (status === 'idle') dispatch(fetchBhajanDataRequest()); }, [dispatch, status]);
  useEffect(() => { if (facetKey && !facet) navigate('/browse', { replace: true }); }, [facetKey, facet, navigate]);

  const allNodes = useMemo(
    () => (catalog?.nodes ?? []),
    [catalog]
  );

  // Non-zero facet values (sorted, with bhajan counts). Values no bhajan
  // references are excluded by deriveFacetValues.
  const facetValues = useMemo(() => (facet ? deriveFacetValues(allNodes, facet) : []), [allNodes, facet]);
  const valueIds = useMemo(() => new Set(facetValues.map((v) => v.nodeId)), [facetValues]);
  // Search is scoped to ONLY this facet's node type (and only values with ≥1 bhajan).
  const facetNodes = useMemo(() => allNodes.filter((n) => valueIds.has(n.nodeId)), [allNodes, valueIds]);

  const filtered = useFilteredNodes(facetNodes, activeFilters, facetDefinitions);
  const shown = useMemo(() => {
    const byId = new Map(facetValues.map((v) => [v.nodeId, v]));
    // Alphabetical, except while an affirmative search is active — then
    // relevance order (matches jnana-native), same contract as the bhajan
    // browser.
    return orderResults(filtered.nodes, activeFilters)
      .map((n) => byId.get(n.nodeId))
      .filter((v): v is FacetValue => !!v);
  }, [facetValues, filtered.nodes, activeFilters]);

  const loading = status === 'loading' || (status === 'idle' && !catalog);

  if (!facet) return null;

  // Capture the optional image resolver so TS keeps the narrowing inside the
  // map callback below (no non-null assertion needed).
  const renderImage = facet.image;

  return (
    <main id="main">
      <section
        aria-label={facet.pluralLabel}
        className="relative border-b border-light"
        style={{ background: APP.browseHeroBackground }}
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 text-center">
          <nav className="text-small text-text-tertiary mb-3">
            <Link to="/browse" className="hover:text-text-secondary">Browse</Link> / {facet.pluralLabel}
          </nav>
          <h1 className="text-h1 font-display font-semibold mb-2">{facet.pluralLabel}</h1>
          <p className="text-text-secondary mb-6">
            {loading
              ? 'Loading…'
              : `${filtered.filteredCount.toLocaleString()} of ${facetValues.length.toLocaleString()} ${facet.pluralLabel.toLowerCase()}`}
          </p>
          <div className="max-w-2xl mx-auto">
            <FilterBar
              filterSet={activeFilters}
              onFilterSetChange={setFilters}
              nodes={facetNodes}
              definitions={facetDefinitions}
              availableFilters={['name', 'nodeId']}
              placeholder={`Search ${facet.pluralLabel.toLowerCase()} by name…`}
              autoFocus
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        {renderImage ? (
          // Image facets (deities) render as uniform cards.
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {shown.map((v) => (
              <Link
                key={v.nodeId}
                to={facetFilterHref(facet, v.nodeId)}
                className="flex flex-col items-center text-center rounded-2xl border border-light bg-bg-2/60 hover:bg-white/5 hover:border-strong transition-colors p-5"
              >
                <img
                  src={renderImage(v.nodeId)}
                  alt=""
                  aria-hidden
                  width={72}
                  height={72}
                  loading="lazy"
                  className="h-[72px] w-[72px] rounded-full bg-bg-3/60 object-cover mb-3"
                />
                <span className="text-text-primary font-medium capitalize leading-tight">{v.name}</span>
                <span className="text-small text-text-tertiary tabular-nums mt-1">
                  {v.bhajanCount} bhajan{v.bhajanCount === 1 ? '' : 's'}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <ul className="divide-y divide-light/40 rounded-2xl border border-light overflow-hidden">
            {shown.map((v) => (
              <li key={v.nodeId}>
                <Link
                  to={facetFilterHref(facet, v.nodeId)}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-white/5 transition-colors"
                >
                  <span className="text-text-primary capitalize">{v.name}</span>
                  <span className="text-small text-text-tertiary tabular-nums">{v.bhajanCount}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {!loading && shown.length === 0 && (
          <p className="py-8 text-center text-text-tertiary">No {facet.pluralLabel.toLowerCase()} found.</p>
        )}
      </div>
    </main>
  );
};

export default MetadataBrowseList;
